import { LayoutGrid, List, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { Filters, ViewMode } from "../mock/types";
import { PROFILES, PROJECTS, SPRINTS, STAGES } from "../mock/data";

const selectClass =
	"h-9 rounded-lg border border-border-strong bg-surface px-3 text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

const EMPTY: Filters = {
	projectId: "",
	sprintId: "",
	userId: "",
	status: "",
	search: "",
};

export function TasksV2Filters({
	filters,
	onChange,
	view,
	onViewChange,
}: {
	filters: Filters;
	onChange: (next: Filters) => void;
	view: ViewMode;
	onViewChange: (view: ViewMode) => void;
}) {
	const set = (patch: Partial<Filters>) =>
		onChange({ ...filters, ...patch });

	const hasFilters =
		filters.projectId ||
		filters.sprintId ||
		filters.userId ||
		filters.status ||
		filters.search;

	return (
		<div className="sticky top-0 z-20 -mx-6 mb-6 border-b border-border bg-surface/80 px-6 py-3 backdrop-blur">
			<div className="flex flex-wrap items-center gap-2">
				<div className="relative">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
					<Input
						value={filters.search}
						onChange={(e) => set({ search: e.target.value })}
						placeholder="Search tasks..."
						className="w-56 pl-9"
					/>
				</div>

				<select
					value={filters.projectId}
					onChange={(e) => set({ projectId: e.target.value })}
					className={selectClass}
				>
					<option value="">All projects</option>
					{PROJECTS.map((p) => (
						<option key={p.id} value={p.id}>
							{p.name}
						</option>
					))}
				</select>

				<select
					value={filters.sprintId}
					onChange={(e) => set({ sprintId: e.target.value })}
					className={selectClass}
				>
					<option value="">All sprints</option>
					{SPRINTS.map((s) => (
						<option key={s.id} value={s.id}>
							{s.name}
						</option>
					))}
				</select>

				<select
					value={filters.userId}
					onChange={(e) => set({ userId: e.target.value })}
					className={selectClass}
				>
					<option value="">All assignees</option>
					{PROFILES.map((p) => (
						<option key={p.id} value={p.id}>
							{p.full_name ?? p.email}
						</option>
					))}
				</select>

				{view === "list" && (
					<select
						value={filters.status}
						onChange={(e) => set({ status: e.target.value })}
						className={selectClass}
					>
						<option value="">All statuses</option>
						{STAGES.map((s) => (
							<option key={s.key} value={s.key}>
								{s.name}
							</option>
						))}
					</select>
				)}

				{hasFilters && (
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onChange(EMPTY)}
					>
						<X className="h-3.5 w-3.5" />
						Clear
					</Button>
				)}

				<div className="ml-auto flex items-center rounded-lg border border-border-strong bg-surface p-0.5">
					<button
						type="button"
						onClick={() => onViewChange("board")}
						className={cn(
							"flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
							view === "board"
								? "bg-primary text-primary-foreground"
								: "text-muted hover:text-foreground",
						)}
					>
						<LayoutGrid className="h-3.5 w-3.5" />
						Board
					</button>
					<button
						type="button"
						onClick={() => onViewChange("list")}
						className={cn(
							"flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
							view === "list"
								? "bg-primary text-primary-foreground"
								: "text-muted hover:text-foreground",
						)}
					>
						<List className="h-3.5 w-3.5" />
						List
					</button>
				</div>
			</div>
		</div>
	);
}
